import { apiClient } from '@/api/client';
import { API_ENDPOINTS } from '@/api/endpoints';
import type {
  BulkAddModelsResponse,
  CloudInstanceCreate,
  CloudInstanceResponse,
  CloudInstanceUpdate,
  DeletionReason,
  EvalScoreResponse,
  LifecycleActionResponse,
  ModelAdminResponse,
  ModelHistoryResponse,
  ModelListResponse,
  ModelResponse,
  OpenTofuDeploymentDetailsResponse,
  OpenTofuDownloadUrlResponse,
  RefreshMetadataResponse,
  UserAdminResponse,
} from '@/api/types';
import type {
  EvalScoreValues,
  ModelCreateValues,
  ModelUpdateValues,
} from '@/features/admin/models/schemas';

// --- Users (auth service admin) -------------------------------------

export const adminAuthService = {
  listUsers: () =>
    apiClient.get<UserAdminResponse[]>(API_ENDPOINTS.ADMIN_AUTH.USERS).then((r) => r.data),
  listPending: () =>
    apiClient.get<UserAdminResponse[]>(API_ENDPOINTS.ADMIN_AUTH.USERS_PENDING).then((r) => r.data),
  updateStatus: (id: string, status: string) =>
    apiClient.put(API_ENDPOINTS.ADMIN_AUTH.USER_STATUS(id), { status }).then((r) => r.data),
  promote: (id: string) =>
    apiClient.post(API_ENDPOINTS.ADMIN_AUTH.USER_PROMOTE(id)).then((r) => r.data),
  demote: (id: string) =>
    apiClient.post(API_ENDPOINTS.ADMIN_AUTH.USER_DEMOTE(id)).then((r) => r.data),
  /** Delete a user for *reason* (#538): billing settles by it. */
  removeUser: (id: string, reason: DeletionReason) =>
    apiClient
      .delete(API_ENDPOINTS.ADMIN_AUTH.USER_DELETE(id), { params: { reason } })
      .then((r) => r.data),
};

// --- Cloud instances ------------------------------------------------

export type InstanceStats = {
  total: number;
  active: number;
  deprecated: number;
  by_provider: Record<string, number>;
};

export type CsvUploadResult = {
  created: number;
  updated: number;
  errors: string[];
};

export const adminInstancesService = {
  list: () =>
    apiClient
      .get<CloudInstanceResponse[]>(API_ENDPOINTS.ADMIN.INSTANCES, { params: { limit: 10000 } })
      .then((r) => r.data),
  stats: () =>
    apiClient.get<InstanceStats>(API_ENDPOINTS.ADMIN.INSTANCE_STATS).then((r) => r.data),
  create: (body: CloudInstanceCreate) =>
    apiClient.post<CloudInstanceResponse>(API_ENDPOINTS.ADMIN.INSTANCES, body).then((r) => r.data),
  update: (id: string, body: CloudInstanceUpdate) =>
    apiClient.put<CloudInstanceResponse>(API_ENDPOINTS.ADMIN.INSTANCE(id), body).then((r) => r.data),
  remove: (id: string) =>
    apiClient.delete(API_ENDPOINTS.ADMIN.INSTANCE(id)).then((r) => r.data),
  uploadCsv: (file: File, updateExisting = true) => {
    const form = new FormData();
    form.append('file', file);
    return apiClient
      .post<CsvUploadResult>(API_ENDPOINTS.ADMIN.INSTANCE_UPLOAD_CSV, form, {
        params: { update_existing: updateExisting },
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },
  clearAll: () =>
    apiClient
      .delete<{ message: string }>(API_ENDPOINTS.ADMIN.INSTANCE_CLEAR)
      .then((r) => r.data),
};

// --- Admin models ---------------------------------------------------

export const adminModelsService = {
  // NOTE: there is no `GET /admin/models` endpoint in the recommender spec —
  // admins read the same catalog as everyone else via `GET /models`. Using
  // the public endpoint also means admin + public views share the
  // react-query cache, so refresh/upload mutations propagate everywhere.
  list: () => apiClient.get<ModelResponse[]>(API_ENDPOINTS.MODELS.LIST).then((r) => r.data),
  remove: (id: string) =>
    apiClient.delete(API_ENDPOINTS.ADMIN.MODEL(id)).then((r) => r.data),

  // --- R5 catalog CRUD / lifecycle / history / eval (FE contract §4.2) ---

  // Admin catalog list — GET /admin/models (ModelAdminResponse with
  // status/source). High `limit` keeps the page's client-side facet/sort/paginate.
  listCatalog: (params: { exclude_archived?: boolean; limit?: number; skip?: number }) =>
    apiClient
      .get<ModelListResponse>(API_ENDPOINTS.ADMIN.MODELS, { params })
      .then((r) => r.data),
  create: (body: ModelCreateValues) =>
    apiClient
      .post<ModelAdminResponse>(API_ENDPOINTS.ADMIN.MODELS, body)
      .then((r) => r.data),
  // Bulk add by HF identity — POST /admin/models/bulk. dry_run=true only
  // classifies each id (new/exists/invalid); dry_run=false creates the new
  // ones. Max 1000 identities per request.
  bulkAdd: (identities: string[], dryRun: boolean) =>
    apiClient
      .post<BulkAddModelsResponse>(API_ENDPOINTS.ADMIN.MODELS_BULK, {
        model_identities: identities,
        dry_run: dryRun,
      })
      .then((r) => r.data),
  update: (id: string, body: Partial<ModelUpdateValues>) =>
    apiClient
      .patch<ModelAdminResponse>(API_ENDPOINTS.ADMIN.MODEL(id), body)
      .then((r) => r.data),
  // remove(id) already defined above (DELETE /admin/models/{id}).
  deprecate: (id: string) =>
    apiClient
      .post<LifecycleActionResponse>(API_ENDPOINTS.ADMIN.MODEL_DEPRECATE(id))
      .then((r) => r.data),
  restore: (id: string) =>
    apiClient
      .post<LifecycleActionResponse>(API_ENDPOINTS.ADMIN.MODEL_RESTORE(id))
      .then((r) => r.data),
  archive: (id: string) =>
    apiClient
      .post<LifecycleActionResponse>(API_ENDPOINTS.ADMIN.MODEL_ARCHIVE(id))
      .then((r) => r.data),
  unarchive: (id: string) =>
    apiClient
      .post<LifecycleActionResponse>(API_ENDPOINTS.ADMIN.MODEL_UNARCHIVE(id))
      .then((r) => r.data),
  refreshMetadataOne: (id: string) =>
    apiClient
      .post<RefreshMetadataResponse>(API_ENDPOINTS.ADMIN.MODEL_REFRESH_METADATA(id))
      .then((r) => r.data),
  history: (id: string, params?: { skip?: number; limit?: number }) =>
    apiClient
      .get<ModelHistoryResponse>(API_ENDPOINTS.ADMIN.MODEL_HISTORY(id), { params })
      .then((r) => r.data),
  setEvalScore: (id: string, body: EvalScoreValues) =>
    apiClient
      .post<EvalScoreResponse>(API_ENDPOINTS.ADMIN.MODEL_EVAL_SCORES(id), body)
      .then((r) => r.data),
  // listEvalScores(id) — pending GET endpoint (FE contract §9 Q3).
};

// --- Admin devices --------------------------------------------------

export type DeviceSummary = {
  id: string;
  name?: string;
  vendor?: string;
  memory_gb?: number | null;
};

export const adminDevicesService = {
  list: () =>
    apiClient.get<DeviceSummary[]>(API_ENDPOINTS.ADMIN.DEVICES).then((r) => r.data),
};

// --- Admin OpenTofu cache -------------------------------------------

export type OpenTofuCacheStats = {
  total_deployments?: number;
  total_size_bytes?: number;
  unique_users?: number;
};

export const adminOpenTofuService = {
  list: (page = 1, page_size = 25) =>
    apiClient
      .get(API_ENDPOINTS.ADMIN.OPENTOFU_DEPLOYMENTS, { params: { page, page_size } })
      .then((r) => r.data),
  stats: () =>
    apiClient.get<OpenTofuCacheStats>(API_ENDPOINTS.ADMIN.OPENTOFU_STATS).then((r) => r.data),
  /** Deployment IDs are UUIDs on the wire — the endpoint URL builders'
   *  `number` parameter type is a contract artefact. Accept `string` here
   *  and cast to satisfy the helper signature. */
  detail: (id: string) =>
    apiClient
      .get<OpenTofuDeploymentDetailsResponse>(
        API_ENDPOINTS.ADMIN.OPENTOFU_DETAIL(id as unknown as number),
      )
      .then((r) => r.data),
  remove: (id: string) =>
    apiClient
      .delete(API_ENDPOINTS.ADMIN.OPENTOFU_DETAIL(id as unknown as number))
      .then((r) => r.data),
  /** Fetches the presigned S3 download URL. The endpoint returns
   *  `{ download_url, expires_in, s3_key }` — the URL is short-lived (~60 min)
   *  so it's resolved on demand rather than cached. */
  downloadInfo: (id: string) =>
    apiClient
      .get<OpenTofuDownloadUrlResponse>(
        API_ENDPOINTS.ADMIN.OPENTOFU_DOWNLOAD(id as unknown as number),
      )
      .then((r) => r.data),
};
